
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { freshdeskApiClient } from "@/api/freshdeskApiClient";
import { Loader2, AlertTriangle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

interface ContactFormProps {
  initialName?: string;
  initialEmail?: string;
}

const ContactForm = ({ initialName = "", initialEmail = "" }: ContactFormProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: initialName,
    email: initialEmail,
    subject: "",
    message: ""
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
    // Clear error when user starts typing again
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate form fields - now ALL fields are required
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      console.log("Submitting support ticket with data:", {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        description: formData.message
      });
      
      const response = await freshdeskApiClient.createTicket({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        description: formData.message
      });
      
      console.log("Support ticket creation response:", response);
      
      // Check if it's a mock response (development environment)
      const isMockResponse = response && response._mock === true;
      
      toast({
        title: isMockResponse 
          ? "Demo Mode: Support ticket simulated" 
          : "Support ticket created",
        description: isMockResponse
          ? "This is running in demo mode. In production, this would create a real support ticket."
          : "We'll get back to you as soon as possible",
      });
      
      // Reset the form
      setFormData(prev => ({
        ...prev,
        subject: "",
        message: ""
      }));
      
    } catch (error) {
      console.error("Error submitting support ticket:", error);
      
      // Handle the error more gracefully
      let errorMessage = "There was an error sending your support request. Please try again.";
      
      if (error instanceof Error) {
        errorMessage = `Error: ${error.message}`;
        console.error("Full error details:", error);
      }
      
      setError(errorMessage);
      
      toast({
        title: "Failed to submit",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contact Support</CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-1">Name *</label>
            <Input 
              id="name" 
              placeholder="Your name" 
              value={formData.name}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">Email *</label>
            <Input 
              id="email" 
              type="email" 
              placeholder="Your email" 
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
          </div>
          <div>
            <label htmlFor="subject" className="block text-sm font-medium mb-1">Subject *</label>
            <Input 
              id="subject" 
              placeholder="How can we help?" 
              value={formData.subject}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
          </div>
          <div>
            <label htmlFor="message" className="block text-sm font-medium mb-1">Message *</label>
            <Textarea 
              id="message" 
              placeholder="Describe your issue" 
              rows={4} 
              value={formData.message}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
          </div>
          <Button 
            type="submit" 
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Message'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
